import {
  MdSubject, MdCastForEducation, MdMeetingRoom, MdMenuBook, MdChildCare,
  MdPersonAddAlt1, MdAssignmentTurnedIn, MdEventAvailable, MdPostAdd, MdGrading,
  MdPersonSearch, MdCampaign, MdPayments, MdSwapHoriz, MdVpnKey, MdDirectionsBus,
  MdManageAccounts,
} from 'react-icons/md';

/**
 * «دليل الاستخدام» — how to get a job done, written ONCE and shown three
 * ways: the written steps on the help page, the «ورّيني» walkthrough
 * (Coach.js) that points at the real buttons, and the navbar search.
 *
 * A step points at an element by its `data-guide` name (g('name')).
 * Those names are part of this file's contract: renaming one in a page
 * breaks the walkthrough — `guide-check.cjs` walks every guide in Chrome.
 *
 * Step fields (all optional except title/text):
 *   id         name for `back` / `again` to jump to
 *   target     selector, or a list (the first one visible wins)
 *   via        shown instead while `target` is hidden (the phone menu)
 *   clickOn    what a click must land on to finish the step (default:
 *              target) — a table is lit, any row's ✎ finishes it
 *   wait       'click' (default with a target) | 'fill' (the field has a
 *              value, then «التالي» or Tab) | 'next' (read, then «التالي»)
 *   expect     [method, path] of the save that finishes the step; a
 *              matching save from ANY earlier step jumps here too
 *              (Enter in a form saves without the button)
 *   advanceWhen ({ q }) => bool, polled — e.g. a review popup opened
 *   back       step id to return to when the target disappears after it
 *              was shown (the popup was cancelled, the page changed)
 *   skip       ({ page, q }) => bool, checked on arrival
 *   optional   a 'fill' step that may be left empty
 *   free       the rest of the screen stays usable (dimmed lightly)
 *   list:false not shown in the written steps
 *   final      the success card: `next` (another guide) / `again`
 */

const g = (name) => `[data-guide="${name}"]`;

const ADMIN = ['admin'];
const OFFICE = ['admin', 'register'];
const STAFF = ['admin', 'register', 'teacher'];

/** Step: open a page from the sidebar (skipped when already there). */
const nav = (page, label) => ({
  id: `nav-${page}`,
  target: g(`nav-${page}`),
  via: g('nav-burger'),
  viaText: 'افتح القائمة من هنا أولاً.',
  title: `افتح «${label}»`,
  text: `من القائمة الجانبية، اضغط «${label}».`,
  skip: ({ page: current }) => current === page,
});

const save = (method, path, back, text = 'اضغط «حفظ».') => ({
  target: g('modal-save'), expect: [method, path], back, title: 'حفظ', text,
});

export const GUIDES = [
  {
    id: 'subject-add',
    roles: ADMIN,
    icon: MdSubject,
    tone: 'sky',
    title: 'إضافة مادة دراسية',
    summary: 'مادة جديدة في قائمة مواد المدرسة، مثل «معلوماتية».',
    words: 'ماده مواد اضافه جديده انشاء منهج اضيف اضف',
    pages: ['subjects'],
    steps: [
      nav('subjects', 'المواد'),
      { id: 'open', target: g('subjects-add'), back: 'nav-subjects', title: 'مادة جديدة', text: 'اضغط «+ مادة جديدة».' },
      { target: g('subject-title'), wait: 'fill', back: 'open', title: 'اسم المادة', text: 'اكتب اسم المادة، مثلاً «معلوماتية».' },
      save('POST', /^\/subjects$/, 'open'),
      {
        final: true,
        title: 'أُضيفت المادة',
        text: 'صارت في قائمة مواد المدرسة. لتُدرَّس في شعبة، وكّل بها معلماً من «تدريس المواد».',
        next: 'assign-teachers',
      },
    ],
    notes: [
      'قائمة المواد واحدة للمدرسة كلها. تصبح المادة من مواد الشعبة حين توكّل بها معلماً في تلك الشعبة، أو يغطيها معلم الصف في الابتدائي.',
    ],
  },

  {
    id: 'teacher-add',
    roles: ADMIN,
    icon: MdCastForEducation,
    tone: 'teal',
    title: 'إضافة معلم',
    summary: 'معلم جديد، وحساب دخوله إلى النظام برقم هاتفه.',
    words: 'معلم استاذ مدرس اضافه جديد حساب اضيف اضف',
    pages: ['teachers'],
    steps: [
      nav('teachers', 'المعلمون'),
      { id: 'open', target: g('teachers-add'), back: 'nav-teachers', title: 'معلم جديد', text: 'اضغط «+ معلم جديد».' },
      { target: g('teacher-name'), wait: 'fill', back: 'open', title: 'اسم المعلم', text: 'اكتب اسم المعلم الكامل كما سيظهر في النظام.' },
      {
        target: g('teacher-phone'), wait: 'fill', optional: true, back: 'open', title: 'رقم الهاتف',
        text: 'رقم جوال المعلم: عليه يصله رمز الدخول إلى النظام. يمكنك تركه الآن وإضافته لاحقاً.',
      },
      save('POST', /^\/teachers$/, 'open'),
      {
        final: true,
        title: 'أُضيف المعلم',
        text: 'يظهر الآن في جدول المعلمين. إن أدخلت رقمه، يدخل إلى النظام باسمه ورمز يصله على هاتفه. الخطوة التالية: وزّعه على المواد.',
        next: 'assign-teachers',
      },
    ],
  },

  {
    id: 'section-create',
    roles: ADMIN,
    icon: MdMeetingRoom,
    tone: 'violet',
    title: 'إنشاء شعبة',
    summary: 'شعبة جديدة في صف وفوج، ومعلم صفها إن كانت ابتدائية.',
    words: 'شعبه شعب صف جديده اضافه انشاء فتح اضيف انشئ افتح',
    pages: ['sections'],
    steps: [
      nav('sections', 'الشعب'),
      { id: 'open', target: g('sections-add'), back: 'nav-sections', title: 'شعبة جديدة', text: 'اضغط «+ شعبة جديدة».' },
      { target: g('section-title'), wait: 'fill', back: 'open', title: 'اسم الشعبة', text: 'اكتب اسم الشعبة، مثلاً «أولى» أو «ثانية ذكور». يظهر في كل النظام مع اسم الصف.' },
      { target: g('section-grade'), wait: 'fill', back: 'open', title: 'الصف', text: 'اختر الصف الذي تتبع له الشعبة.' },
      { target: g('section-batch'), wait: 'fill', back: 'open', title: 'الفوج', text: 'اختر الفوج: صباحي أو مسائي.' },
      {
        target: g('section-homeroom'), wait: 'fill', optional: true, back: 'open', title: 'معلم الصف',
        text: 'للصفوف الابتدائية: اختر المعلم الذي يدرّس الشعبة كل المواد. من السابع وما فوق اتركه «بدون معلم صف».',
      },
      save('POST', /^\/sections$/, 'open'),
      {
        final: true,
        title: 'أُنشئت الشعبة',
        text: 'تظهر الآن في جدول الشعب، وتستطيع تسجيل الطلاب فيها. الخطوة التالية: وزّع المعلمين على موادها.',
        next: 'assign-teachers',
      },
    ],
  },

  {
    id: 'assign-teachers',
    roles: ADMIN,
    icon: MdMenuBook,
    tone: 'gold',
    title: 'توزيع المعلمين على مواد الشعبة',
    summary: 'لكل مادة في الشعبة معلمها: هكذا تُفتح المادة في الشعبة ويدرّسها المعلم.',
    words: 'توزيع توكيل تدريس مواد ماده معلم استاذ شعبه اسناد تعيين اضافه مواد للشعبه اوزع وزع اوكل اسند',
    pages: ['assignments'],
    steps: [
      nav('assignments', 'تدريس المواد'),
      {
        id: 'open', target: g('assignments-add'), back: 'nav-assignments', title: 'توكيل جديد',
        text: 'اضغط «+ توكيل جديد». التوكيل = مادة واحدة في شعبة واحدة مع معلمها.',
      },
      { target: g('assign-section'), wait: 'fill', back: 'open', title: 'الشعبة', text: 'اختر الشعبة.' },
      { target: g('assign-subject'), wait: 'fill', back: 'open', title: 'المادة', text: 'اختر المادة التي ستُدرَّس فيها.' },
      { target: g('assign-teacher'), wait: 'fill', back: 'open', title: 'المعلم', text: 'اختر المعلم الذي يدرّسها. لا تجده؟ أضفه أولاً من «المعلمون».' },
      save('POST', /^\/assignments$/, 'open'),
      {
        final: true,
        title: 'تمّ التوكيل',
        text: 'صار المعلم يرى الشعبة في «صفوفي» ويضع علامات هذه المادة فيها. كرّر ذلك لكل مادة من مواد الشعبة.',
        again: { label: 'توكيل مادة أخرى', to: 'open' },
      },
    ],
    notes: [
      'لتغيير معلم مادة: اضغط «تبديل المعلم» في سطرها. يبقى محفوظاً من درّسها قبله.',
      'في الابتدائي لا حاجة لتوكيل كل مادة: يكفي «معلم الصف» في الشعبة (دليل «ضبط الصفوف الابتدائية»).',
    ],
  },

  {
    id: 'primary-setup',
    roles: ADMIN,
    icon: MdChildCare,
    tone: 'rose',
    title: 'ضبط الصفوف الابتدائية',
    summary: 'معلم واحد يدرّس الشعبة كل موادها، ومعلم اختصاص لمادة مثل الإنكليزي عند الحاجة.',
    words: 'ابتدائي ابتدائيه معلم الصف معلم واحد كل المواد اول ثاني ثالث رابع خامس سادس روضه تحضيري ضبط اجهز جهز اضبط',
    pages: ['grades', 'sections', 'assignments'],
    steps: [
      {
        list: false,
        title: 'كيف يعمل الابتدائي',
        text: 'في الابتدائي يدرّس «معلم الصف» شعبته كل المواد. نضبط ذلك في خطوتين: نعلّم الصف أنه ابتدائي، ثم نختار معلم الصف لكل شعبة.',
        nextLabel: 'ابدأ',
      },
      nav('grades', 'الصفوف'),
      {
        id: 'grade-pick',
        target: g('grades-table'),
        clickOn: `${g('grades-table')} ${g('row-edit')}`,
        back: 'nav-grades',
        title: 'اختر الصف',
        text: 'اضغط ✎ بجانب صف ابتدائي، مثلاً «الأول».',
      },
      { target: g('grade-primary'), wait: 'fill', back: 'grade-pick', title: 'نمط ابتدائي', text: 'ضع إشارة على «نمط ابتدائي (معلم واحد لكل المواد)».' },
      save('PATCH', /^\/grades\/\d+$/, 'grade-pick', 'اضغط «حفظ». يظهر الصف في الجدول بعلامة «معلم واحد».'),
      {
        target: g('grades-table'),
        wait: 'next',
        title: 'صار الصف ابتدائياً',
        text: 'كرّر ذلك لباقي صفوف الابتدائي متى شئت. الآن نختار معلم الصف لكل شعبة.',
      },
      nav('sections', 'الشعب'),
      {
        id: 'section-pick',
        target: g('sections-table'),
        clickOn: `${g('sections-table')} ${g('row-edit')}`,
        back: 'nav-sections',
        title: 'اختر الشعبة',
        text: 'اضغط ✎ بجانب شعبة من شعب الصف. اكتب اسم الصف في خانة البحث فوق الجدول لتجدها بسرعة.',
      },
      { target: g('section-homeroom'), wait: 'fill', back: 'section-pick', title: 'معلم الصف', text: 'اختر المعلم الذي يدرّس هذه الشعبة كل موادها.' },
      save('PATCH', /^\/sections\/\d+$/, 'section-pick'),
      {
        wait: 'next',
        title: 'مادة لها معلم اختصاص؟',
        text: 'مثل الإنكليزي: وكّلها لمعلمها من «تدريس المواد» ← «+ توكيل جديد». يأخذ معلم الاختصاص تلك المادة وحدها، ويبقى معلم الصف لباقي المواد.',
      },
      {
        final: true,
        title: 'ضُبطت الشعبة الابتدائية',
        text: 'صار معلم الصف يرى الشعبة في «صفوفي» ويضع علامات كل موادها. كرّر اختيار معلم الصف لكل شعبة ابتدائية.',
        again: { label: 'شعبة أخرى', to: 'section-pick' },
        next: 'assign-teachers',
      },
    ],
    notes: [
      'التوكيل الخاص بمادة يتقدّم على معلم الصف في تلك المادة فقط.',
      'تغيير معلم الصف من «الشعب» ← ✎ ← «معلم الصف»، في أي وقت.',
    ],
  },

  {
    id: 'student-to-section',
    roles: ADMIN,
    icon: MdPersonAddAlt1,
    tone: 'emerald',
    title: 'إضافة طالب إلى صف',
    summary: 'طالب جديد: إضبارته وتسجيله في شعبته دفعة واحدة.',
    words: 'طالب جديد اضافه تسجيل صف شعبه اضباره قيد تلميذ اسجل سجل اضيف ادخل طالبا',
    pages: ['student-directory', 'students'],
    steps: [
      nav('student-directory', 'شؤون الطلاب'),
      {
        id: 'tab',
        target: g('dir-view-new'),
        back: 'nav-student-directory',
        skip: ({ q }) => q(g('dir-view-new'))?.getAttribute('aria-selected') === 'true',
        title: 'إضبارة جديدة',
        text: 'اضغط «إضبارة جديدة».',
      },
      {
        target: g('dz-student.first_name'), wait: 'fill', back: 'tab', title: 'اسم الطالب',
        text: 'اكتب اسم الطالب. اسمه الكامل يُركَّب وحده من اسمه واسم أبيه والكنية.',
      },
      {
        target: [g('dz-family.father_name'), g('dz-familyId')],
        wait: 'fill',
        back: 'tab',
        title: 'العائلة',
        text: 'اكتب اسم الأب ثم الكنية. إن كانت العائلة مسجّلة من قبل يقترحها النظام، فاضغط «ربط الطالب بها».',
      },
      {
        target: g('dz-student.section_id'), wait: 'fill', back: 'tab', title: 'الشعبة: هنا تختار الصف',
        text: 'اكتب اسم الصف أو الشعبة، مثلاً «السابع»، ثم اختر من القائمة. هكذا يُسجَّل الطالب في هذه الشعبة.',
      },
      {
        id: 'confirm',
        target: g('dz-confirm'),
        free: true,
        advanceWhen: ({ q }) => !!q(g('dz-add')),
        back: 'tab',
        title: 'تأكيد',
        text: 'باقي الحقول تكملها الآن أو لاحقاً. اضغط «تأكيد» لمراجعة الإضبارة.',
      },
      { target: g('dz-add'), expect: ['POST', /^\/students$/], back: 'confirm', title: 'إضافة', text: 'راجع البيانات، ثم اضغط «إضافة».' },
      {
        final: true,
        title: 'أُضيف الطالب إلى شعبته',
        text: 'ظهر رقم تسجيله، وتستطيع طباعة إضبارته الآن. صار في تفقّد الشعبة، وتجده في «الإضبارات».',
      },
    ],
    notes: [
      'طالب مسجّل من قبل وليس في شعبة؟ دليل «تسجيل طالب مسجّل في شعبة».',
      'Ctrl + Enter يؤكّد الإضبارة من أي مكان في الصفحة.',
    ],
  },

  {
    id: 'student-enroll',
    roles: OFFICE,
    icon: MdAssignmentTurnedIn,
    tone: 'sky',
    title: 'تسجيل طالب مسجّل في شعبة',
    summary: 'طالب موجود في النظام وليس في شعبة: تقيّده في شعبته.',
    words: 'قيد قيود تسجيل طالب شعبه صف ادخال اقيد',
    pages: ['enrollments'],
    steps: [
      nav('enrollments', 'القيود'),
      { id: 'open', target: g('enrollments-add'), back: 'nav-enrollments', title: 'قيد جديد', text: 'اضغط «+ قيد جديد».' },
      { target: g('enroll-student'), wait: 'fill', back: 'open', title: 'الطالب', text: 'اختر الطالب.' },
      { target: g('enroll-section'), wait: 'fill', back: 'open', title: 'الشعبة', text: 'اختر الشعبة.' },
      { target: g('enroll-date'), wait: 'fill', back: 'open', title: 'تاريخ البدء', text: 'من هذا اليوم يُحسب الطالب في الشعبة: في تفقّدها وامتحاناتها.' },
      save('POST', /^\/enrollments$/, 'open', 'اضغط «قيد».'),
      { final: true, title: 'قُيّد الطالب', text: 'صار الطالب في الشعبة من تاريخ البدء.' },
    ],
    notes: [
      'لنقل طالب من شعبة أو صف إلى آخر استخدم دليل «نقل طالب إلى شعبة أو صف آخر»، لا قيداً جديداً.',
    ],
  },

  /* ---------------- the teacher's day ---------------- */

  {
    id: 'teacher-attendance',
    roles: ['teacher'],
    icon: MdEventAvailable,
    tone: 'emerald',
    title: 'أخذ التفقّد',
    summary: 'حضور شعبتك اليوم بضغطة على كل غائب، ثم حفظ.',
    words: 'تفقد حضور غياب غائب تاخير متاخر دوام سجل الحضور اخذ اسجل',
    pages: ['teacher-home'],
    steps: [
      nav('teacher-home', 'صفوفي'),
      {
        id: 'pick', target: g('th-classes'), clickOn: g('th-take'), back: 'nav-teacher-home',
        title: 'اختر الشعبة', text: 'في بطاقة الشعبة اضغط «أخذ التفقّد».',
      },
      {
        target: g('th-reg-grid'), wait: 'next', free: true, back: 'pick', title: 'علّم الغائبين',
        text: 'الكل «حاضر» في البداية. اضغط على الطالب الغائب مرة فيصير «غائب»، ومرة ثانية «متأخر».',
      },
      {
        target: g('th-reg-save'), expect: ['POST', /^\/attendance$/], back: 'pick',
        skip: ({ q }) => !q(g('th-reg-save')),
        title: 'حفظ التفقّد', text: 'اضغط «حفظ التفقّد». بعدها يعرض عليك الانتقال إلى الشعبة التالية مباشرة.',
      },
      {
        final: true,
        title: 'هكذا يُؤخذ التفقّد',
        text: 'بعد الحفظ تصير الشعبة «تم التفقّد». لتصحيح حالة طالب بعد الحفظ اضغط «تعديل حالة طالب».',
        next: 'exam-create',
      },
    ],
    notes: [
      'الشعب التي لم يُؤخذ تفقّدها اليوم يذكّرك بها الشريط الأحمر أعلى «صفوفي».',
    ],
  },

  {
    id: 'exam-create',
    roles: STAFF,
    icon: MdPostAdd,
    tone: 'violet',
    title: 'إنشاء امتحان',
    summary: 'مذاكرة أو شفهي أو نصفي لمادة في شعبة، قبل رصد علاماته.',
    words: 'امتحان مذاكره اختبار شفهي نصفي جديد انشاء علامات انشئ اضيف',
    pages: ['exams'],
    steps: [
      nav('exams', 'الامتحانات'),
      { id: 'open', target: g('exams-add'), back: 'nav-exams', title: 'امتحان جديد', text: 'اضغط «امتحان جديد».' },
      { target: g('exam-grade'), wait: 'fill', back: 'open', title: 'الصف', text: 'اختر الصف.' },
      { target: g('exam-section'), wait: 'fill', back: 'open', title: 'الشعبة', text: 'اختر الشعبة.' },
      { target: g('exam-subject'), wait: 'fill', back: 'open', title: 'المادة', text: 'اختر المادة.' },
      { target: g('exam-type'), wait: 'fill', back: 'open', title: 'نوع الامتحان', text: 'مذاكرة أولى، شفهي، امتحان نصفي…' },
      { target: g('exam-total'), wait: 'fill', back: 'open', title: 'العلامة العظمى', text: 'تأتي من نوع الامتحان، وتستطيع تغييرها. والتاريخ فوقها هو اليوم ما لم تغيّره.' },
      save('POST', /^\/exams$/, 'open', 'اضغط «إنشاء الامتحان».'),
      {
        final: true,
        title: 'أُنشئ الامتحان',
        text: 'يظهر في الجدول «مسودة». الخطوة التالية: رصد العلامات.',
        next: 'exam-marks',
      },
    ],
  },

  {
    id: 'exam-marks',
    roles: STAFF,
    icon: MdGrading,
    tone: 'gold',
    title: 'رصد العلامات ونشرها',
    summary: 'درجات الطلاب في امتحان، ثم نشرها لتظهر لهم ولأهاليهم.',
    words: 'علامات درجات رصد ادخال نشر امتحان مذاكره نتائج ارصد ادخل انشر اعلن',
    pages: ['exams'],
    steps: [
      nav('exams', 'الامتحانات'),
      {
        id: 'pick', target: g('exams-table'), clickOn: `${g('exams-table')} ${g('exam-marks')}`, back: 'nav-exams',
        title: 'افتح العلامات', text: 'اضغط «العلامات» في سطر الامتحان.',
      },
      {
        target: g('marks-edit'), advanceWhen: ({ q }) => !!q(g('marks-upload')), back: 'pick',
        title: 'بدء الرصد', text: 'اضغط «تعديل العلامات».',
      },
      {
        target: g('marks-table'), wait: 'next', free: true, back: 'pick', title: 'اكتب الدرجات',
        text: 'اكتب درجة كل طالب في عمود «الدرجة»، والنسبة تُحسب وحدها. الملاحظة اختيارية.',
      },
      {
        target: g('marks-upload'), expect: ['POST', /^\/exams\/\d+\/results(\/bulk)?$/], back: 'pick',
        title: 'رفع العلامات', text: 'اضغط «رفع العلامات» فتُحفظ الدرجات كلها دفعة واحدة.',
      },
      {
        target: g('modal-close'), advanceWhen: ({ q }) => !q('.ui-modal'),
        title: 'إغلاق', text: 'حُفظت الدرجات. أغلق النافذة.',
      },
      {
        id: 'publish',
        target: g('exams-table'),
        clickOn: `${g('exams-table')} ${g('exam-publish')}`,
        skipLabel: 'أنشر لاحقاً',
        title: 'النشر',
        text: 'حين تكتمل العلامات اضغط زر القفل 🔒 في سطر الامتحان. بعد النشر تظهر للطلاب وأهاليهم وتُقفل.',
      },
      { target: g('confirm-ok'), expect: ['POST', /^\/exams\/\d+\/publish$/], back: 'publish', title: 'تأكيد النشر', text: 'اضغط «نشر».' },
      {
        final: true,
        title: 'نُشرت العلامات',
        text: 'صارت ظاهرة للطلاب وأهاليهم في بوابة الطالب وتطبيق الهاتف.',
        laterTitle: 'حُفظت العلامات',
        laterText: 'الامتحان «مسودة» حتى تنشره: زر القفل 🔒 في سطره متى اكتملت العلامات.',
      },
    ],
    notes: [
      '«رفع العلامات» يحفظ الدرجات الجديدة فقط. لتغيير درجة محفوظة استخدم زر الحفظ في سطر الطالب.',
    ],
  },

  {
    id: 'find-student',
    roles: STAFF,
    icon: MdPersonSearch,
    tone: 'sky',
    title: 'الوصول إلى ملف طالب',
    summary: 'اسم الطالب أو هاتف أهله في البحث أعلى الشاشة، فيُفتح ملفه كاملاً.',
    words: 'بحث ابحث طالب ملف معلومات هاتف اهل ولي امر اجد الاقي اعرف افتح',
    pages: ['student-directory'],
    steps: [
      {
        target: g('top-search'), wait: 'fill', title: 'البحث',
        text: 'اكتب اسم الطالب أو جزءاً منه، أو رقم هاتف أحد والديه. (Ctrl + K يفتح البحث من أي صفحة)',
      },
      {
        target: g('top-search'),
        advanceWhen: ({ page, q }) => page === 'student-directory' && !!q(g('sd-hero')),
        title: 'اختر الطالب', text: 'اضغط على اسمه في النتائج، أو Enter لأول نتيجة.',
      },
      {
        target: g('sd-hero'), wait: 'next', free: true, title: 'ملف الطالب',
        text: 'شعبته وحضوره اليوم في الأعلى، وأزرار الطباعة بجانبه. وتحته: الحضور والعلامات والقيود.',
      },
      { final: true, title: 'هذا ملف الطالب', text: 'من البحث نفسه تصل أيضاً إلى الشعب والمعلمين والعائلات والصفحات.' },
    ],
  },

  {
    id: 'notify-send',
    roles: STAFF,
    icon: MdCampaign,
    tone: 'rose',
    title: 'إرسال إشعار',
    summary: 'رسالة تصل هواتف الطلاب وأهاليهم: لشعبة أو صف أو طالب.',
    words: 'اشعار رساله تنبيه ارسال اهل اولياء امور هاتف تبليغ ارسل ابعث ابلغ اشعارا',
    pages: ['notifications'],
    steps: [
      nav('notifications', 'الإشعارات'),
      { id: 'open', target: g('notif-add'), back: 'nav-notifications', title: 'إشعار جديد', text: 'اضغط «+ إشعار جديد».' },
      {
        target: g('notif-audience'), wait: 'next', back: 'open', title: 'إلى من؟',
        text: 'اختر من يصله الإشعار: كل الطلاب، أو صفوف، أو شعب، أو طلاب محددون.',
      },
      {
        target: g('notif-pick'), wait: 'fill', back: 'open', skip: ({ q }) => !q(g('notif-pick')), title: 'حدّدهم',
        text: 'ابحث واختر من القائمة، ولك أن تختار أكثر من واحد. وتحتها «إرسال لأولياء الأمور أيضاً».',
      },
      { target: g('notif-title'), wait: 'fill', back: 'open', title: 'العنوان', text: 'عنوان قصير وواضح.' },
      { target: g('notif-message'), wait: 'fill', back: 'open', title: 'الرسالة', text: 'اكتب نص الرسالة.' },
      {
        target: g('notif-next'), advanceWhen: ({ q }) => !!q(g('notif-send')), back: 'open',
        title: 'مراجعة', text: 'اضغط «التالي: مراجعة» لترى كم شخصاً سيصلهم.',
      },
      { target: g('notif-send'), expect: ['POST', /^\/notifications\/send$/], back: 'open', title: 'إرسال', text: 'راجع، ثم اضغط «إرسال الآن».' },
      {
        final: true,
        title: 'أُرسل الإشعار',
        text: 'يصل إلى هواتفهم، ومن لم يفعّل الإشعارات يجده داخل التطبيق. وتعرف من قرأه من قائمة الإشعارات.',
      },
    ],
  },

  /* ---------------- the office's day ---------------- */

  {
    id: 'payment-add',
    roles: OFFICE,
    icon: MdPayments,
    tone: 'gold',
    title: 'قبض قسط وطباعة الوصل',
    summary: 'دفعة من ملف الطالب، والوصل جاهز للطباعة فوراً.',
    words: 'قسط دفعه دفع قبض فلوس رسوم وصل ايصال طباعه مال اقبض ادفع اطبع قسطا',
    pages: ['student-directory', 'payments'],
    steps: [
      nav('student-directory', 'شؤون الطلاب'),
      {
        id: 'tab', target: g('dir-view-search'), back: 'nav-student-directory',
        skip: ({ q }) => q(g('dir-view-search'))?.getAttribute('aria-selected') === 'true',
        title: 'البحث عن طالب', text: 'اضغط «البحث عن طالب».',
      },
      { id: 'find', target: g('sd-search'), wait: 'fill', back: 'tab', title: 'ابحث عن الطالب', text: 'اكتب اسم الطالب أو هاتف أحد والديه.' },
      { target: g('sd-list'), clickOn: `${g('sd-list')} .sd-row`, back: 'tab', title: 'اختر الطالب', text: 'اضغط على اسمه في القائمة.' },
      { id: 'pay', target: g('sd-pay'), back: 'find', title: 'قبض قسط', text: 'اضغط «قبض قسط».' },
      { target: g('pay-amount'), wait: 'fill', back: 'pay', title: 'المبلغ', text: 'اكتب المبلغ بالدولار.' },
      save('POST', /^\/payments$/, 'pay', 'اضغط «قبض وطباعة الإيصال».'),
      { target: g('doc-print'), wait: 'next', free: true, title: 'الوصل', text: 'هذا وصل الدفعة. اضغط «طباعة» لطباعته.' },
      { final: true, title: 'قُبض القسط', text: 'الدفعة في ملف الطالب (تبويب «الأقساط») وفي صفحة «الأقساط».' },
    ],
    notes: [
      'في صفحة «الأقساط» قائمة المتأخرين عن الشهر الحالي، وبجانب كل منهم «تسجيل دفعة».',
    ],
  },

  {
    id: 'student-transfer',
    roles: OFFICE,
    icon: MdSwapHoriz,
    tone: 'teal',
    title: 'نقل طالب إلى شعبة أو صف آخر',
    summary: 'من شعبة إلى شعبة، في الصف نفسه أو إلى صف آخر، مع بقاء سجله محفوظاً.',
    words: 'نقل تحويل طالب شعبه اخرى تغيير الشعبه انقل احول طالبا صف اخر تغيير الصف ترفيع من صف لصف',
    pages: ['students'],
    steps: [
      nav('students', 'الطلاب'),
      {
        id: 'pick', target: g('students-table'), clickOn: `${g('students-table')} ${g('student-move')}`, back: 'nav-students',
        title: 'اختر الطالب', text: 'اكتب اسمه في البحث فوق الجدول، ثم اضغط «نقل» في سطره.',
      },
      { target: g('move-section'), wait: 'fill', back: 'pick', title: 'الشعبة الجديدة', text: 'اختر الشعبة الجديدة. للنقل إلى صف آخر غيّر «الصف» فوقها أولاً.' },
      save('POST', /^\/students\/transfer$/, 'pick', 'اضغط «نقل».'),
      { final: true, title: 'نُقل الطالب', text: 'صار في شعبته الجديدة من اليوم، وبقي سجله في الشعبة القديمة محفوظاً.' },
    ],
  },

  {
    id: 'parent-code',
    roles: OFFICE,
    icon: MdVpnKey,
    tone: 'violet',
    title: 'رمز دخول لولي الأمر',
    summary: 'لمن لا تصله رسائل الرمز: رمز ثابت يُعطى فوراً أو يُرسل بالواتساب.',
    words: 'رمز دخول كود ولي امر اب ام حساب بوابه الطالب واتساب كلمه سر لا يصله اعطي تصله رساله الرمز',
    pages: ['student-directory'],
    steps: [
      nav('student-directory', 'شؤون الطلاب'),
      {
        id: 'tab', target: g('dir-view-search'), back: 'nav-student-directory',
        skip: ({ q }) => q(g('dir-view-search'))?.getAttribute('aria-selected') === 'true',
        title: 'البحث عن طالب', text: 'اضغط «البحث عن طالب».',
      },
      { id: 'find', target: g('sd-search'), wait: 'fill', back: 'tab', title: 'ابحث عن الطالب', text: 'اكتب اسم الطالب أو هاتف أحد والديه.' },
      { target: g('sd-list'), clickOn: `${g('sd-list')} .sd-row`, back: 'tab', title: 'اختر الطالب', text: 'اضغط على اسمه في القائمة.' },
      { id: 'logins', target: g('sd-logins'), back: 'find', title: 'حسابات الدخول', text: 'اضغط «حسابات الدخول».' },
      {
        target: g('logins-list'), clickOn: `${g('logins-list')} ${g('login-issue')}`,
        expect: ['POST', /^\/users\/\d+\/access-code$/], back: 'logins',
        title: 'إعطاء الرمز', text: 'عند الأب أو الأم اضغط «إعطاء رمز دخول».',
      },
      {
        target: g('logins-list'), wait: 'next', free: true, title: 'أرسله لولي الأمر',
        text: 'يظهر الرمز فوراً مع اسم الدخول: انسخه مع التعليمات، أو اضغط «إرسال واتساب».',
      },
      { final: true, title: 'أُعطي رمز الدخول', text: 'يدخل ولي الأمر إلى بوابة الطالب باسم الدخول وهذا الرمز.' },
    ],
  },

  {
    id: 'bus-add',
    roles: OFFICE,
    icon: MdDirectionsBus,
    tone: 'sky',
    title: 'إضافة باص',
    summary: 'سيارة وسائقها وأجرتها الشهرية، ثم يُختار الباص في إضبارة الطالب.',
    words: 'باص سياره سائق مواصلات نقل اجره اضيف',
    pages: ['transportation'],
    steps: [
      nav('transportation', 'المواصلات'),
      { id: 'open', target: g('buses-add'), back: 'nav-transportation', title: 'سيارة جديدة', text: 'اضغط «+ سيارة جديدة».' },
      { target: g('bus-driver'), wait: 'fill', back: 'open', title: 'السائق', text: 'اكتب اسم السائق.' },
      { target: g('bus-number'), wait: 'fill', back: 'open', title: 'رقم السيارة', text: 'رقم السيارة كما يعرفه الأهل والطلاب.' },
      {
        target: g('bus-fee'), wait: 'fill', optional: true, back: 'open', title: 'الأجرة الشهرية',
        text: 'بالدولار. تُضاف وحدها إلى قسط كل طالب يركب هذا الباص.',
      },
      save('POST', /^\/transportation$/, 'open'),
      {
        final: true,
        title: 'أُضيف الباص',
        text: 'اختره في إضبارة الطالب (حقل «الباص») فتُضاف أجرته إلى أقساطه.',
      },
    ],
  },

  {
    id: 'user-add',
    roles: ADMIN,
    icon: MdManageAccounts,
    tone: 'teal',
    title: 'حساب دخول لموجه أو مدير',
    summary: 'حساب جديد للإدارة، يدخل صاحبه باسمه ورمز يصله على هاتفه.',
    words: 'مستخدم حساب موجه مدير اداره دخول اضافه اضيف',
    pages: ['users'],
    steps: [
      nav('users', 'المستخدمون'),
      { id: 'open', target: g('users-add'), back: 'nav-users', title: 'مستخدم جديد', text: 'اضغط «+ مستخدم جديد».' },
      { target: g('user-name'), wait: 'fill', back: 'open', title: 'الاسم الكامل', text: 'كما سيكتبه صاحبه عند الدخول.' },
      { target: g('user-phone'), wait: 'fill', back: 'open', title: 'رقم الهاتف', text: 'عليه يصله رمز الدخول.' },
      { target: g('user-role'), wait: 'fill', back: 'open', title: 'الدور', text: 'موجه أو مدير.' },
      save('POST', /^\/users$/, 'open', 'اضغط «إضافة».'),
      {
        final: true,
        title: 'أُضيف الحساب',
        text: 'يدخل باسمه ورمز يصله على هاتفه. إن لم تصله الرسائل أعطه «رمز الدخول» من زر المفتاح في سطره.',
      },
    ],
  },
];

/** How people ask («كيف بعمل…», «كيف ضيف…»): matched by every guide. */
export const ASK_WORDS = 'كيف طريقه شرح اضيف اضف ضيف اعمل بعمل اسوي انشئ افتح اسجل سجل ادخل دخل اوزع وزع اربط اختار اغير';

export const GUIDE_BY_ID = Object.fromEntries(GUIDES.map((x) => [x.id, x]));

/**
 * The «ابدأ من هنا» path at the top of the help page, one per role.
 * «جهّز مدرستك» is the order a new school is set up in: what a later
 * guide picks from (subjects, teachers, sections) comes first.
 */
export const PATHS = [
  {
    roles: ADMIN,
    title: 'جهّز مدرستك',
    sub: 'بهذا الترتيب: المواد والمعلمون أولاً، ثم الشعب وتوزيع المواد، ثم الطلاب.',
    guides: ['subject-add', 'teacher-add', 'section-create', 'assign-teachers', 'primary-setup', 'student-to-section'],
  },
  {
    roles: ['register'],
    title: 'عملك اليومي في شؤون الطلاب',
    sub: 'أكثر ما يمرّ على الموجه كل يوم، خطوة بخطوة.',
    guides: ['parent-code', 'student-transfer', 'notify-send'],
  },
  {
    roles: ['teacher'],
    title: 'يومك مع صفوفك',
    sub: 'التفقّد صباحاً، ثم الامتحانات والعلامات، والتواصل مع الأهل.',
    guides: ['teacher-attendance', 'exam-create', 'exam-marks', 'find-student', 'notify-send'],
  },
];

export const pathFor = (role) => PATHS.find((p) => p.roles.includes(role)) || null;

/** The guides this role can follow: every page they use must be open to it
 *  (`nav` is already filtered by role, so the role itself is optional). */
export function guidesFor(role, nav) {
  const can = new Set(nav.map((p) => p.id));
  return GUIDES.filter((x) => (!role || x.roles.includes(role)) && x.pages.every((p) => can.has(p)));
}

/** The written form of a guide: what each step says, in order. */
export const writtenSteps = (guide) => guide.steps.filter((s) => !s.final && s.list !== false);
